import React from 'react';
import { PlusIcon, CogIcon } from '@heroicons/react/24/solid';
import { Button } from "../ui/button"; // Assuming Button is in ui/components/ui/button
import SidebarToggle from './SidebarToggle';
import DataSourceList from './DataSourceList';
import AgentList from './AgentList';
import ToolCallList from './ToolCallList';
import GPT5Settings from '../Settings/GPT5Settings';

/**
 * Renders the main chat sidebar container, including data sources, agents, tool calls,
 * and controls for adding/managing them.
 * 
 * @param {object} props - Component props.
 * @param {boolean} props.sidebarOpen - Current visibility state of the sidebar.
 * @param {function} props.setSidebarOpen - Function to update the sidebar visibility state.
 * @param {Array} props.dataSources - Array of data source objects.
 * @param {Array} props.agents - Array of agent objects.
 * @param {Array} props.apiCalls - Array of tool call objects.
 * @param {object} props.expandedApiCalls - Object mapping tool call index to expanded state.
 * @param {function} props.toggleApiCall - Function to toggle tool call expansion.
 * @param {function} props.setShowAddDataSourceModal - Function to open the Add Data Source modal.
 * @param {function} props.handleOpenAgentModal - Function to open the Agent Management modal and force remount.
 * @param {string} props.sidebarError - Error message related to sidebar data loading.
 * @param {boolean} props.isSidebarLoading - Loading state for sidebar data.
 * @param {function} props.handleEditDataSource - Function to initiate editing a data source.
 * @param {function} props.handleDeleteDataSourceClick - Function to initiate deleting a data source.
 * @param {function} props.handleViewSchema - Function to initiate viewing database schema.
 * @param {boolean} props.enableAgentManagement - Flag to control visibility of agent features.
 * @param {string} props.reasoningEffort - Current GPT-5 reasoning effort setting.
 * @param {string} props.verbosity - Current GPT-5 verbosity setting.
 * @param {function} props.setReasoningEffort - Function to update reasoning effort.
 * @param {function} props.setVerbosity - Function to update verbosity level.
 * @param {function} props.resetGPT5Parameters - Function to reset GPT-5 parameters to defaults.
 * @param {boolean} props.isGPT5Enabled - Whether GPT-5 features are enabled.
 */
export default function ChatSidebar({
    sidebarOpen,
    setSidebarOpen,
    dataSources,
    agents,
    apiCalls,
    expandedApiCalls,
    toggleApiCall,
    setShowAddDataSourceModal,
    handleOpenAgentModal, // Use the new handler prop name
    sidebarError, // Receive error state
    isSidebarLoading, // Receive loading state
    handleEditDataSource, // Receive edit handler
    handleDeleteDataSourceClick, // Receive delete handler
    handleViewSchema, // Receive schema view handler
    enableAgentManagement, // <-- Receive the feature flag prop
    // GPT-5 Settings props
    reasoningEffort,
    verbosity,
    setReasoningEffort,
    setVerbosity,
    resetGPT5Parameters,
    isGPT5Enabled
}) {
    // Determine the translation class based on sidebarOpen state
    const translationClass = sidebarOpen ? 'translate-x-0' : 'translate-x-full';

    return (
        <div className={`absolute right-0 top-0 bottom-0 transition-transform duration-300 ease-in-out ${translationClass}`}>
            {/* Toggle button positioned relative to the sliding container */}
            <SidebarToggle sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

            {/* Sidebar content area */}
            <div className="w-72 h-full bg-gray-50 border-l border-gray-200 flex flex-col shadow-lg"> {/* Added shadow */}
                {/* Scrollable content area */}
                <div className="flex-1 p-4 overflow-y-auto space-y-6">

                    {/* Display error message if any */}
                    {sidebarError && (
                         <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
                             <strong>Error loading sidebar data:</strong> {sidebarError}
                         </div>
                    )}

                    {/* Data Sources Section */}
                    <section aria-labelledby="data-sources-heading">
                        <div className="flex justify-between items-center mb-3">
                            <h2 id="data-sources-heading" className="text-lg font-semibold text-gray-800">Data Sources</h2>
                            <Button variant="outline" size="sm" onClick={() => setShowAddDataSourceModal(true)} aria-label="Add new data source">
                                <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" /> Add
                            </Button>
                        </div>
                        {isSidebarLoading ? (
                             <p className="text-sm text-gray-600 italic">Loading data sources...</p>
                        ) : (
                            <DataSourceList
                                dataSources={dataSources}
                                handleEditDataSource={handleEditDataSource}
                                handleDeleteDataSourceClick={handleDeleteDataSourceClick} // Pass delete handler down
                                handleViewSchema={handleViewSchema} // Pass schema view handler down
                            />
                        )}
                    </section>

                    {/* Agent Management Section - Conditionally Rendered */}
                    {enableAgentManagement && (
                        <section aria-labelledby="agents-heading">
                            <div className="flex justify-between items-center mb-3">
                                <h2 id="agents-heading" className="text-lg font-semibold text-gray-800">Agents</h2>
                                <Button variant="outline" size="sm" onClick={handleOpenAgentModal} aria-label="Manage agents"> {/* Call the new handler */}
                                    <CogIcon className="h-4 w-4 mr-1" aria-hidden="true" /> Manage
                                </Button>
                            </div>
                            {isSidebarLoading ? (
                                <p className="text-sm text-gray-600 italic">Loading agents...</p>
                            ) : (
                                <AgentList agents={agents} />
                            )}
                        </section>
                    )}

                    {/* GPT-5 Settings Section - Conditionally Rendered */}
                    {isGPT5Enabled && (
                        <section aria-labelledby="gpt5-settings-heading">
                            <h2 id="gpt5-settings-heading" className="text-lg font-semibold mb-3 text-gray-800">GPT-5 Settings</h2>
                            <GPT5Settings
                                reasoningEffort={reasoningEffort}
                                verbosity={verbosity}
                                setReasoningEffort={setReasoningEffort}
                                setVerbosity={setVerbosity}
                                resetGPT5Parameters={resetGPT5Parameters}
                                isGPT5Enabled={isGPT5Enabled}
                                className="mb-4"
                            />
                        </section>
                    )}

                    {/* Tool Calls Section */}
                    <section aria-labelledby="tool-calls-heading">
                        <h2 id="tool-calls-heading" className="text-lg font-semibold mb-3 text-gray-800">Tool Calls</h2>
                        {/* No separate loading state needed here as apiCalls are managed by useChatManager */}
                        <ToolCallList 
                            apiCalls={apiCalls} 
                            expandedApiCalls={expandedApiCalls} 
                            toggleApiCall={toggleApiCall} 
                        />
                    </section>

                </div>
            </div>
        </div>
    );
}